'use client';

import { createContext, startTransition, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { NextIntlClientProvider } from 'next-intl';

const I18nCtx = createContext(null);
const DEFAULT_TIME_ZONE = 'Europe/Paris';
const IGNORED_INTL_ERROR_CODES = new Set(['MISSING_MESSAGE']);

export const useI18n = () => {
    const ctx = useContext(I18nCtx);
    if (!ctx) throw new Error('useI18n must be used within I18nClientProvider');
    return ctx;
};

export function I18nClientProvider({ children, initialLocale, messagesByLocale }) {
    const [locale, setLocaleState] = useState(initialLocale);
    const [isSwitching, setIsSwitching] = useState(false);
    const messages = messagesByLocale[locale] ?? messagesByLocale.fr ?? {};

    // API publique pour changer la langue sans reload
    const setLocale = useCallback((l) => {
        setIsSwitching(true);
        startTransition(() => {
            setLocaleState(l);
        });
    }, []);

    useEffect(() => {
        setIsSwitching(false);
    }, [locale]);

    const contextValue = useMemo(() => ({
        locale,
        setLocale,
        isSwitching,
    }), [locale, setLocale, isSwitching]);

    return (
        <I18nCtx.Provider value={contextValue}>
            <NextIntlClientProvider
                locale={locale}
                messages={messages}
                timeZone={DEFAULT_TIME_ZONE}
                onError={(error) => {
                    // On garde le comportement non bloquant
                    if (!IGNORED_INTL_ERROR_CODES.has(error.code)) {
                        console.error(error);
                    }
                }}
                getMessageFallback={({ namespace, key }) => {
                    const ns = namespace ? `${namespace}.` : '';
                    if (!key) return `[${ns}undefined]`;
                    console.groupCollapsed(
                        `%c🔍 Traduction manquante : "${ns}${key}"`,
                        'color: orange;',
                    );
                    console.log('Locale :', locale);
                    console.log('Namespace :', namespace ?? 'global');
                    console.log('Clé :', key);
                    console.trace('Stack trace');
                    console.groupEnd();
                    return `[${ns}${key}]`;
                }}
            >
                {children}
            </NextIntlClientProvider>
        </I18nCtx.Provider>
    );
}
